import { useState, useCallback, useRef } from 'react';

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
}

interface RateLimitState {
  isLimited: boolean;
  remainingRequests: number;
  resetTime: number | null;
}

const DEFAULT_CONFIG: RateLimitConfig = {
  maxRequests: 5,
  windowMs: 60000, // 1 minute
};

/**
 * Client-side rate limiting hook to prevent API abuse
 * This provides a first line of defense before server-side rate limiting
 */
export const useRateLimit = (config: RateLimitConfig = DEFAULT_CONFIG) => {
  const requestTimestamps = useRef<number[]>([]);
  const [state, setState] = useState<RateLimitState>({
    isLimited: false,
    remainingRequests: config.maxRequests,
    resetTime: null,
  });

  const checkRateLimit = useCallback((): boolean => {
    const now = Date.now();
    const windowStart = now - config.windowMs;

    // Remove expired timestamps
    requestTimestamps.current = requestTimestamps.current.filter(
      (timestamp) => timestamp > windowStart
    );

    const currentCount = requestTimestamps.current.length;
    const remaining = config.maxRequests - currentCount;
    const isLimited = currentCount >= config.maxRequests;

    // Calculate reset time
    let resetTime: number | null = null;
    if (isLimited && requestTimestamps.current.length > 0) {
      resetTime = requestTimestamps.current[0] + config.windowMs;
    }

    setState({
      isLimited,
      remainingRequests: Math.max(0, remaining),
      resetTime,
    });

    return !isLimited;
  }, [config.maxRequests, config.windowMs]);

  const recordRequest = useCallback((): boolean => {
    if (!checkRateLimit()) {
      return false;
    }

    requestTimestamps.current.push(Date.now());
    
    // Update state after recording
    const remaining = config.maxRequests - requestTimestamps.current.length;
    setState((prev) => ({
      ...prev,
      remainingRequests: Math.max(0, remaining),
    }));

    return true;
  }, [checkRateLimit, config.maxRequests]);

  const getRemainingTime = useCallback((): number => {
    if (!state.resetTime) return 0;
    return Math.max(0, state.resetTime - Date.now());
  }, [state.resetTime]);

  const formatRemainingTime = useCallback((): string => {
    const remainingMs = getRemainingTime();
    if (remainingMs <= 0) return '0s';
    
    const seconds = Math.ceil(remainingMs / 1000);
    if (seconds < 60) return `${seconds}s`;
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }, [getRemainingTime]);

  const reset = useCallback(() => {
    requestTimestamps.current = [];
    setState({
      isLimited: false,
      remainingRequests: config.maxRequests,
      resetTime: null,
    });
  }, [config.maxRequests]);

  return {
    ...state,
    checkRateLimit,
    recordRequest,
    getRemainingTime,
    formatRemainingTime,
    reset,
  };
};

// Preset configurations for different operations
export const RATE_LIMIT_CONFIGS = {
  // Temp mail generation - 5 requests per minute
  tempMail: { maxRequests: 5, windowMs: 60000 },
  // Admin operations - 10 requests per minute
  adminOperations: { maxRequests: 10, windowMs: 60000 },
  // General API calls - 30 requests per minute
  general: { maxRequests: 30, windowMs: 60000 },
  // Search operations - 20 requests per minute
  search: { maxRequests: 20, windowMs: 60000 },
} as const;
